import { getDocumentationUrl } from '@gonfalon/docs/documentationUrl';
import { Message } from '@gonfalon/messages/Message';
import { useGetVegaIntegrationStatusQuery } from '@gonfalon/observability-graphql/hooks';
import { Box } from '@gonfalon/observability-ui/components/Box';
import { AI_BOT_NAME } from '@gonfalon/observability-ui/constants';
import { useProjectId } from '@gonfalon/observability-ui/hooks/useProjectId';
import { Alert, Heading, Text } from '@launchpad-ui/components';

import * as styles from './AgentInsightsPanel.css';

/**
 * Whether this member is blocked from Vega entirely.
 *
 * Both the drawer and the standalone conversation gate on this, so it lives in one hook. Apollo's
 * default cache-first policy means the second caller reads the first one's result rather than
 * issuing another request.
 */
export function useVegaAccessDenied(): boolean {
  const { projectId } = useProjectId();
  const { error } = useGetVegaIntegrationStatusQuery({
    variables: { project_id: projectId },
    skip: !projectId,
  });

  return !!error?.message?.includes('access denied');
}

/** Shown in place of any Vega surface when the member lacks the `talkToVega` action. */
export function VegaPermissionDeniedAlert() {
  return (
    <Box display="flex" alignItems="center" justifyContent="center" padding="16" cssClass={styles.centeredAlertBox}>
      <Alert status="error">
        <Heading slot="title">
          <Message id="vega.permissionDenied.title" />
        </Heading>
        <Text>
          <Message
            id="vega.permissionDenied.body"
            values={{
              botName: AI_BOT_NAME,
              code: (chunks) => <code>{chunks}</code>,
              link: (chunks) => (
                <a
                  href={getDocumentationUrl('home/getting-started/vega#roles-and-rbac')}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {chunks}
                </a>
              ),
            }}
          />
        </Text>
      </Alert>
    </Box>
  );
}
